import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

// Ученики без email (записаны преподавателем «вручную», например по имени из
// мессенджера) получают служебный адрес в этом домене: вся система — кредиты,
// история, баланс — держится на userEmail, а писем на такой адрес не шлём.
export const OFFLINE_DOMAIN = 'no-email.local';

export const isOfflineEmail = (email) => !!email && email.endsWith('@' + OFFLINE_DOMAIN);

export const makeOfflineEmail = () =>
  `student-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}@${OFFLINE_DOMAIN}`;

const normalize = (s) => String(s || '').toLowerCase().replace(/ё/g, 'е').trim();

// Поле «Ученик»: поиск по имени/email с выпадающим списком, клавиатура ↑ ↓ Enter Esc.
// Если такого ученика нет — последним пунктом предлагается «Новый ученик».
// value: null | { email, name, isNew }
export default function StudentPicker({ students, value, onChange, allowNew = true, autoFocus = false, placeholder = 'Имя или email ученика' }) {
  const [queryText, setQueryText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const options = useMemo(() => {
    const q = normalize(queryText);
    let list;
    if (!q) {
      // Без запроса — сначала те, у кого урок был/будет недавно: их выбирают чаще всего.
      list = [...students].sort((a, b) => (b.lastLessonAt || '').localeCompare(a.lastLessonAt || '') || a.name.localeCompare(b.name));
    } else {
      list = students
        .filter((s) => normalize(s.name).includes(q) || (!isOfflineEmail(s.email) && normalize(s.email).includes(q)))
        .sort((a, b) => {
          const aStarts = normalize(a.name).startsWith(q) ? 0 : 1;
          const bStarts = normalize(b.name).startsWith(q) ? 0 : 1;
          return aStarts - bStarts || a.name.localeCompare(b.name);
        });
    }
    const opts = list.map((s) => ({ kind: 'student', student: s }));
    const exact = students.some((s) => normalize(s.name) === q || normalize(s.email) === q);
    if (allowNew && q && !exact) opts.push({ kind: 'new', name: queryText.trim() });
    return opts;
  }, [students, queryText, allowNew]);

  useEffect(() => { setActive(0); }, [queryText]);

  useEffect(() => {
    const el = listRef.current?.children[active];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const choose = (opt) => {
    if (!opt) return;
    if (opt.kind === 'new') {
      const typed = opt.name;
      // Ввели email целиком — считаем это email, а не именем.
      if (typed.includes('@')) onChange({ isNew: true, name: '', email: typed.toLowerCase() });
      else onChange({ isNew: true, name: typed, email: '' });
    } else {
      onChange({ isNew: false, name: opt.student.name, email: opt.student.email });
    }
    setQueryText('');
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { if (open && options[active]) { e.preventDefault(); choose(options[active]); } }
    else if (e.key === 'Escape') { if (open) { e.stopPropagation(); setOpen(false); } }
  };

  if (value) {
    const known = students.find((s) => s.email === value.email);
    return (
      <div className="adm-picked">
        <div className="adm-avatar">{(value.name || value.email || '?').slice(0, 1).toUpperCase()}</div>
        <div className="adm-picked-text">
          <div className="adm-picked-name">
            {value.name || value.email}
            {value.isNew && <span className="adm-tag adm-tag-new">новый</span>}
          </div>
          <div className="adm-picked-sub">
            {value.isNew ? 'будет добавлен в список учеников' : (isOfflineEmail(value.email) ? 'без email' : value.email)}
            {known && known.balance !== undefined && known.hasCredits && (
              <span className={'adm-balance ' + (known.balance > 0 ? 'pos' : known.balance < 0 ? 'neg' : 'zero')}>баланс {known.balance}</span>
            )}
          </div>
        </div>
        <button type="button" className="adm-icon-btn" title="Выбрать другого" onClick={() => { onChange(null); setTimeout(() => inputRef.current?.focus(), 0); }}>✕</button>
      </div>
    );
  }

  return (
    <div className="adm-combo" ref={wrapRef}>
      <input
        ref={inputRef}
        className="adm-input"
        type="text"
        value={queryText}
        placeholder={placeholder}
        autoFocus={autoFocus}
        onChange={(e) => { setQueryText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-controls={listId}
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && options.length > 0 && (
        <ul className="adm-combo-list" id={listId} ref={listRef} role="listbox">
          {options.map((opt, i) => (
            <li
              key={opt.kind === 'new' ? '__new' : opt.student.email}
              role="option"
              aria-selected={i === active}
              className={'adm-combo-item' + (i === active ? ' active' : '') + (opt.kind === 'new' ? ' is-new' : '')}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => { e.preventDefault(); choose(opt); }}
            >
              {opt.kind === 'new' ? (
                <>
                  <span className="adm-avatar adm-avatar-new">+</span>
                  <span className="adm-combo-main">Новый ученик: <strong>{opt.name}</strong></span>
                </>
              ) : (
                <>
                  <span className="adm-avatar">{opt.student.name.slice(0, 1).toUpperCase()}</span>
                  <span className="adm-combo-main">
                    <span className="adm-combo-name">{opt.student.name}</span>
                    <span className="adm-combo-sub">{isOfflineEmail(opt.student.email) ? 'без email' : opt.student.email}</span>
                  </span>
                  {opt.student.hasCredits && (
                    <span className={'adm-balance ' + (opt.student.balance > 0 ? 'pos' : opt.student.balance < 0 ? 'neg' : 'zero')}>{opt.student.balance}</span>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
